/**
 * API 呼び出しの薄いラッパ。
 *
 * リクエスト/レスポンスの型は `make gen-types`（openapi-typescript）が生成する
 * `src/lib/api/schema.d.ts` を使い、手書きしない（指示書 §0-5）。
 * ここではエラー形式（§8）の正規化とセッションCookie送信の既定値だけを担う。
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/** 指示書 §8 の統一エラー形式 */
export interface ApiErrorBody {
  code: string;
  message: string;
  field_errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string[]> | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.fieldErrors = body.field_errors;
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.code === 'string' && typeof candidate.message === 'string';
}

const CSRF_COOKIE_NAME = 'cxo_csrf';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

/** double submit 用の CSRF トークン（httpOnly ではない Cookie から読む） */
export function readCsrfToken(): string | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${CSRF_COOKIE_NAME}=`));
  return match ? decodeURIComponent(match.slice(CSRF_COOKIE_NAME.length + 1)) : null;
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  };

  if (!SAFE_METHODS.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      // 認証はセッションCookie（httpOnly）。常に送る（指示書 §4）
      credentials: 'include',
      ...init,
      headers,
    });
  } catch {
    // 圏外・Wi-Fi切断・サーバー停止。ここを素通しすると画面には
    // 「読み込みに失敗しました」としか出ず、利用者が原因を判断できない（D-083）
    throw new ApiError(0, {
      code: 'network_error',
      message:
        'インターネットに接続できませんでした。通信状況を確認して、もう一度お試しください。',
    });
  }

  const payload: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const wrapped = (payload as { error?: unknown } | null)?.error;
    if (res.status === 401 && !path.startsWith('/auth/')) {
      // 放置してログインが切れただけの状態を「エラー」に見せない
      throw new ApiError(401, {
        code: 'session_expired',
        message: 'ログインの有効期限が切れました。お手数ですが、もう一度ログインしてください。',
      });
    }
    throw new ApiError(
      res.status,
      isApiErrorBody(wrapped)
        ? wrapped
        : {
            code: 'unknown_error',
            message:
              'うまく通信できませんでした。少し時間をおいて、もう一度お試しください。（解消しない場合は事務局にご連絡ください）',
          },
    );
  }

  return payload as T;
}

export { API_BASE_URL };
